'use client'

import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api-fetch'

const SHORT_DRAMA_ACCESS_CHANGED_EVENT = 'aimanju:short-drama-access-changed'

let cachedAccess: { enabled: boolean; expiresAt: number } | null = null
let inflightAccess: Promise<boolean> | null = null
const ACCESS_CACHE_TTL_MS = 60_000

export function emitShortDramaAccessChanged(enabled: boolean) {
  cachedAccess = { enabled, expiresAt: Date.now() + ACCESS_CACHE_TTL_MS }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SHORT_DRAMA_ACCESS_CHANGED_EVENT, { detail: { enabled } }))
  }
}

async function fetchShortDramaAccess(): Promise<boolean> {
  if (cachedAccess && cachedAccess.expiresAt > Date.now()) return cachedAccess.enabled
  if (inflightAccess) return inflightAccess

  inflightAccess = (async () => {
    const response = await apiFetch('/api/short-drama/access')
    if (!response.ok) return true
    const payload = await response.json() as { enabled?: boolean }
    const enabled = payload.enabled !== false
    cachedAccess = { enabled, expiresAt: Date.now() + ACCESS_CACHE_TTL_MS }
    return enabled
  })().finally(() => {
    inflightAccess = null
  })

  return inflightAccess
}

export function useShortDramaAccess(defaultEnabled = true) {
  const [enabled, setEnabled] = useState(defaultEnabled)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    fetchShortDramaAccess()
      .then((nextEnabled) => {
        if (!cancelled) setEnabled(nextEnabled)
      })
      .catch(() => {
        if (!cancelled) setEnabled(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail
      if (typeof detail?.enabled === 'boolean') {
        setEnabled(detail.enabled)
        setLoading(false)
      } else {
        void fetchShortDramaAccess().then(setEnabled).finally(() => setLoading(false))
      }
    }
    window.addEventListener(SHORT_DRAMA_ACCESS_CHANGED_EVENT, handleChange)
    return () => {
      cancelled = true
      window.removeEventListener(SHORT_DRAMA_ACCESS_CHANGED_EVENT, handleChange)
    }
  }, [])

  return { enabled, loading }
}
